/*eslint-env browser*/
import topLeftAndPivot from "./topleft_and_pivot.js"
import addDiscretizerCapabilities from "./discretizer.js"
import autoDimensions from "./auto_dimensions.js"
import Color from "./color.js"
import addScreenImageImportExport from "./screen_image_import_export.js"

const {getTopLeftPoint, copyPositionParam, addPositionAccessors} = topLeftAndPivot

const {autoConvertToRGB255} = Color

/*eslint-disable no-magic-numbers */

function ScreenImage (params) {

    if ("onLoad" in params) { //FIXME remove soon
        params.whenLoaded = params.onLoad
        console.warn("Deprecation for screen images - onLoad has been replaced with whenLoaded")
    }

    const {source, whenLoaded = () => {}} = params

    checkInconsistencyError(params)

    if (source) {
        importFromSource(this, source, params)
    } else {
        init(this, params)
        whenLoaded(this)
    }

    if (!this.syncFromSource) {
        this.syncFromSource = () => {}
    }

}


ScreenImage.instancePixelMethods = null

ScreenImage.directPixelMethodsOnPrototype = [
    "makeOpaque",
    "fillWithColor"
]


function checkInconsistencyError (params) {
    if ("pixelSize" in params) {
        if ("widthInPixels" in params) {
            throw new Error("You can not set widthInPixels when pixelSize is specified")
        }
        if ("heightInPixels" in params) {
            throw new Error("You can not set heightInPixels when pixelSize is specified")
        }
    }
}


const {importFromSource} = addScreenImageImportExport(ScreenImage, init)


function init (screenImage, params) { // eslint-disable-line complexity
    let {widthInPixels, heightInPixels, useOpacity = true} = params

    const {imageData} = screenImage
    if (imageData) {
        widthInPixels  = imageData.width
        heightInPixels = imageData.height
        screenImage.widthInPixels  = widthInPixels
        screenImage.heightInPixels = heightInPixels
    } else {
        if (widthInPixels) {
            widthInPixels = Math.round(widthInPixels)
        }
        if (heightInPixels) {
            heightInPixels = Math.round(heightInPixels)
        }
    }

    initPositioningAndDiscretizer(screenImage, params, {widthInPixels, heightInPixels})

    screenImage.addOnePixelMethods = function () {
        let tracker

        if (!ScreenImage.instancePixelMethods) {
            tracker = Object.keys(screenImage)
        }

        addOnePixelReaderMethods(screenImage)
        addOnePixelModifierMethods(screenImage, {useOpacity})

        if (!ScreenImage.instancePixelMethods) {
            let newTracker = Object.keys(screenImage)
            ScreenImage.instancePixelMethods = newTracker.filter(property => !tracker.includes(property))
        }
    }


    if (useOpacity) {
        screenImage.fillWithColor = fillWithColor
    } else {
        screenImage.fillWithColor = fillWithColorNoOpacity
    }


    if (!params.bare) {
        if (!imageData) {
            initBlackImageData(screenImage)
        }
        screenImage.addOnePixelMethods()

        addPositionAccessors(screenImage, {whenChanged: () => addDiscretizationMethods(screenImage)})
    }

}





//************************ WRITE PIXELS


ScreenImage.prototype.setColorForEachPixel = function (getColor) {
    const {pixelsCount, gridIndexToCoords, setColorAtIndex} = this
    for (let i = 0; i < pixelsCount; i++) {
        const {x, y} = gridIndexToCoords(i)
        setColorAtIndex(i, getColor(x, y))
    }
}


ScreenImage.prototype.setColorForEachPixelUsingPixelCoords = function (getColor) {
    const {pixelsCount, gridIndexToGridCoords, setColorAtIndex} = this
    for (let i = 0; i < pixelsCount; i++) {
        const {x, y} = gridIndexToGridCoords(i)
        setColorAtIndex(i, getColor(x, y))
    }
}


ScreenImage.prototype.setColorForEachPixelUsingIndex = function (getColor) {
    const {pixelsCount, setColorAtIndex} = this
    for (let i = 0; i < pixelsCount; i++) {
        setColorAtIndex(i, getColor(i))
    }
}


ScreenImage.prototype.modifyColors = function (filter) {
    const {pixelsCount, getColorAtIndex, setColorAtIndex} = this
    for (let i = 0; i < pixelsCount; i++) {
        setColorAtIndex(i, filter(getColorAtIndex(i)))
    }
}


ScreenImage.prototype.makeOpaque = function () {
    this.touched = true
    const {pixelsCount, imageDataData} = this
    const dataLength = pixelsCount * 4
    for (let index = 0; index < dataLength; index += 4) {
        imageDataData[index + 3] = 255
    }
}

function fillWithColor (...params) {
    this.touched = true
    const {red, green, blue, opacity = 255} = autoConvertToRGB255(...params)

    const {pixelsCount, imageDataData} = this
    const dataLength = pixelsCount * 4

    for (let index = 0; index < dataLength; index += 4) {
        imageDataData[index]     = red
        imageDataData[index + 1] = green
        imageDataData[index + 2] = blue
        imageDataData[index + 3] = opacity
    }
}

function fillWithColorNoOpacity (...params) {
    this.touched = true
    const {red, green, blue} = autoConvertToRGB255(...params)
    const {pixelsCount, imageDataData} = this
    const dataLength = pixelsCount * 4

    for (let index = 0; index < dataLength; index += 4) {
        imageDataData[index]     = red
        imageDataData[index + 1] = green
        imageDataData[index + 2] = blue
    }
}


ScreenImage.prototype.fillWithCheckers = function () {
    this.touched = true
    const {pixelsCount, imageDataData, gridIndexToGridCoords} = this
    for (let i = 0; i < pixelsCount; i++) {
        const index = i * 4

        const {x, y} = gridIndexToGridCoords(i)
        let light = 0
        if ((x % 2) + (y % 2) === 1) {
            light = 255
        }
        imageDataData[index]     = light
        imageDataData[index + 1] = light
        imageDataData[index + 2] = light
    }
}



ScreenImage.prototype.removeBackground = function (backgroundColors) {

    if (!Array.isArray(backgroundColors)) {
        backgroundColors = [backgroundColors]
    }

    backgroundColors = backgroundColors.map(color => {
        if (!(color instanceof Color)) {
            color = new Color(color)
        }
        return color
    })

    function removeBackgroundColors (color) {

        for (let i = 0; i < backgroundColors.length; i += 1) {
            if (color.equals(backgroundColors[i])) {
                return {
                    red:   0,
                    green: 0,
                    blue:  0,
                    opacity: 0
                }
            }
        }

        return color
    }

    this.modifyColors(removeBackgroundColors)
}






//************************ INIT



function initBlackImageData (screenImage) {
    const {widthInPixels, heightInPixels} = screenImage
    screenImage.imageData     = new ImageData(widthInPixels, heightInPixels)
    screenImage.imageDataData = screenImage.imageData.data
    screenImage.makeOpaque()
}



//************************ POSITION AND DISCRETIZATION


//FIXME beta. rename ?
ScreenImage.prototype.getPosition = function () {
    const {width, height} = this

    const topLeft = getTopLeftPoint(this, width, height)

    if (!topLeft) {
        return null
    }

    return {
        topLeft,
        center: {
            x: topLeft.x + width  / 2,
            y: topLeft.y - height / 2
        },
        width,
        height,
        orientation: 0 //FIXME
    }
}


ScreenImage.prototype.updatePosition = function (params) {
    console.warn("ScreenImage.updatePosition is deprecated... Now you can just do screenImage.center = {x, y} or screenImage.width = 56")
    setPosition(this, params, this)
}


function initPositioningAndDiscretizer (screenImage, params, {widthInPixels, heightInPixels}) {
    setPosition(screenImage, params, {widthInPixels, heightInPixels})


    if (widthInPixels === undefined && heightInPixels !== undefined) {
        widthInPixels = Math.round(heightInPixels * screenImage.width / screenImage.height)
    }

    if (widthInPixels !== undefined && heightInPixels === undefined) {
        heightInPixels = Math.round(widthInPixels * screenImage.height / screenImage.width)
    }

    const {pixelSize} = params

    const {xCellsCount, yCellsCount} = addDiscretizationMethods(screenImage, {
        widthInPixels,
        heightInPixels,
        pixelSize
    })

    screenImage.widthInPixels  = xCellsCount
    screenImage.heightInPixels = yCellsCount

    screenImage.pixelsCount = xCellsCount * yCellsCount

    initDimensionsAccessors(screenImage)

}




function setPosition (screenImage, params, {widthInPixels, heightInPixels}) {

    const {width = screenImage.width, height = screenImage.height, orientation} = params

    if (orientation) {
        throw new Error("orientation not yet implemented for ScreenImage")
    }

    const newDimensions = autoDimensions({
        width,
        height,
        sourceWidth:  widthInPixels,
        sourceHeight: heightInPixels
    })

    screenImage.width  = newDimensions.width
    screenImage.height = newDimensions.height


    copyPositionParam(params, screenImage, {replace: true})
}


function initDimensionsAccessors (screenImage) {
    let {width, height} = screenImage
    Object.defineProperty(screenImage, "width", {
        get: () => width,
        set: (value) => {
            if (width !== value) {
                width = value
                addDiscretizationMethods(screenImage)
            }
        },
        enumerable:   true,
        configurable: true
    })

    Object.defineProperty(screenImage, "height", {
        get: () => height,
        set: (value) => {
            if (height !== value) {
                height = value
                addDiscretizationMethods(screenImage)
            }
        },
        enumerable:   true,
        configurable: true
    })

    screenImage.batchUpdatePosition = function (params) {
        let {center} = screenImage
        if (
            width  !== params.width ||
            height !== params.height ||
            center.x !== params.center.x ||
            center.y !== params.center.y
        ) {
            width  = params.width
            height = params.height
            screenImage.center = params.center
        }
    }
}





function addDiscretizationMethods (
    screenImage, {
        pixelSize,
        widthInPixels  = screenImage.widthInPixels,
        heightInPixels = screenImage.heightInPixels
    } = {}
) {
    const position = screenImage.getPosition()

    // An image without a world anchor still gets a pixel grid (so pixel/index
    // iteration works), but built from a placeholder frame: its world-coordinate
    // methods are meaningless and get replaced with explicit errors right after.
    const {width, height, topLeft} = position || unplacedFrame(screenImage)
    const {x: xMin, y: yMax} = topLeft

    const cellsCounts = addDiscretizerCapabilities(
        screenImage,
        {
            xMin,
            xMax: xMin + width,
            yMin: yMax - height,
            yMax,
            xCellsCount: widthInPixels,
            yCellsCount: heightInPixels,
            cellSize:    pixelSize
        }
    )

    if (position) {
        screenImage.pixelCoordsToCoords = screenImage.gridCoordsToCoords
        screenImage.coordsToPixelCoords = screenImage.discretize
    } else {
        disableWorldCoordsMethods(screenImage)
    }

    if (screenImage.whenDiscretizationChanged) {
        screenImage.whenDiscretizationChanged()
    }

    return cellsCounts
}


// Origin-anchored frame sized from the image's world dimensions. Only used to
// size the pixel grid of an unplaced image; its world-coordinate methods are
// discarded afterwards (see disableWorldCoordsMethods), so the anchor is moot.
function unplacedFrame (screenImage) {
    const {width, height} = screenImage
    return {
        width,
        height,
        topLeft: {x: 0, y: 0}
    }
}


// An unplaced image has a pixel grid but no place in the world, hence no world
// coordinates. Converting to/from world coords is meaningless, so replace those
// methods with a clear error rather than returning bogus coordinates.
function disableWorldCoordsMethods (screenImage) {
    function noWorldCoords () {
        const error = new Error(
            "This image has no position in the world, so it has no world coordinates. " +
            "Give it a position (for example center: {x: 0, y: 0}), or work in pixel coordinates: " +
            "setColorForEachPixelUsingPixelCoords, setColorAtPixelCoords, getColorAtPixelCoords."
        )
        error.frLocale =
            "Cette image n'a pas de position dans le monde, elle n'a donc pas de coordonnées monde. " +
            "Donne-lui une position (par exemple center: {x: 0, y: 0}), ou travaille en coordonnées pixel : " +
            "setColorForEachPixelUsingPixelCoords, setColorAtPixelCoords, getColorAtPixelCoords."
        throw error
    }

    screenImage.discretize          = noWorldCoords
    screenImage.gridIndexToCoords   = noWorldCoords
    screenImage.gridCoordsToCoords  = noWorldCoords
    screenImage.pixelCoordsToCoords = noWorldCoords
    screenImage.coordsToPixelCoords = noWorldCoords
}




//************************ PIXEL READ / WRITE

function addOnePixelReaderMethods (screenImage) {
    let {widthInPixels, imageDataData} = screenImage


    screenImage.getFastColorAtIndex = function (index) {
        index *= 4
        return {
            red:     imageDataData[index]     / 255,
            green:   imageDataData[index + 1] / 255,
            blue:    imageDataData[index + 2] / 255,
            opacity: imageDataData[index + 3] / 255
        }
    }


    screenImage.getFastColorAtPixelCoords = function (pixCoords) {
        return screenImage.getFastColorAtIndex(pixCoords.x + pixCoords.y * widthInPixels)
    }


    screenImage.getFastColorAt = function (coords) {
        const newCoords = this.discretize(coords)
        if (newCoords) {
            return screenImage.getFastColorAtIndex(newCoords.x + newCoords.y * widthInPixels)
        } else {
            return null
        }
    }


    screenImage.getColorAtIndex = function (index) {
        index *= 4
        return new Color(
            imageDataData[index]     / 255,
            imageDataData[index + 1] / 255,
            imageDataData[index + 2] / 255,
            imageDataData[index + 3] / 255
        )
    }


    screenImage.getColorAtPixelCoords = function (pixCoords) {
        return screenImage.getColorAtIndex(pixCoords.x + pixCoords.y * widthInPixels)
    }


    screenImage.getColorAt = function (coords) {
        const newCoords = this.discretize(coords)
        if (newCoords) {
            return screenImage.getColorAtIndex(newCoords.x + newCoords.y * widthInPixels)
        } else {
            return null
        }
    }
}


function addOnePixelModifierMethods (screenImage, {useOpacity}) {

    let {widthInPixels, imageDataData} = screenImage


    let untouched = true


    if (useOpacity) {
        screenImage.setColorAtIndex = function (index, color) {
            if (untouched) {
                screenImage.touched = true
                untouched = false
            }
            index *= 4
            const {red, green, blue, opacity = 255} = autoConvertToRGB255(color)
            imageDataData[index]     = red
            imageDataData[index + 1] = green
            imageDataData[index + 2] = blue
            imageDataData[index + 3] = opacity
        }
    } else {
        screenImage.setColorAtIndex = function (index, color) {
            if (untouched) {
                screenImage.touched = true
                untouched = false
            }
            index *= 4
            const rgb = autoConvertToRGB255(color)
            imageDataData[index]     = rgb.red
            imageDataData[index + 1] = rgb.green
            imageDataData[index + 2] = rgb.blue
        }
    }


    const {setColorAtIndex} = screenImage

    //for faster access
    screenImage.setLight255AtIndex = function (index, light255) {
        if (untouched) {
            screenImage.touched = true
            untouched = false
        }
        index *= 4
        imageDataData[index]     = light255
        imageDataData[index + 1] = light255
        imageDataData[index + 2] = light255
    }


    screenImage.setColorAtPixelCoords = function (pixCoords, color) {
        setColorAtIndex((pixCoords.x + pixCoords.y * widthInPixels), color)
    }


    screenImage.setColorAt = function (coords, color) {
        const newCoords = screenImage.discretize(coords)
        if (newCoords) {
            setColorAtIndex((newCoords.x + newCoords.y * widthInPixels), color)
        }
    }

}




export default ScreenImage
